import React, { useState, useEffect, useMemo } from "react";
import { UserPlus, X, Search, Loader2, Users } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Admin-only widget on AdminProjectDetail. Lets the admin add/remove EXTRA agents
 * who appear in or have access to a project (multi-makelaar shoots from one office).
 *
 * Co-agents see the request in their dashboard, can review previews, leave timestamped
 * comments, and download deliverables — but they don't own the project (no edit rights
 * on schedule/cancel).
 */
export default function CoAgentsManager({ req, onSaved }) {
    const [saving, setSaving] = useState(false);
    const [allAgents, setAllAgents] = useState([]);
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);

    // Fetch all approved agents on mount (small list — admins typically have <100 agents)
    useEffect(() => {
        let cancelled = false;
        api.get("/admin/users")
            .then(({ data }) => {
                if (cancelled) return;
                const agents = (data || []).filter((u) => u.role === "agent" && u.approval_status === "approved");
                setAllAgents(agents);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const ownerId = req?.user_id;
    const currentIds = useMemo(() => new Set(req?.co_agent_ids || []), [req]);
    const currentList = useMemo(() => req?.co_agents || [], [req]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return allAgents
            .filter((a) => a.id !== ownerId && !currentIds.has(a.id))
            .filter((a) =>
                !q ||
                (a.name || "").toLowerCase().includes(q) ||
                (a.email || "").toLowerCase().includes(q) ||
                (a.company || "").toLowerCase().includes(q),
            )
            .slice(0, 50);
    }, [allAgents, currentIds, ownerId, search]);

    const update = async (newIds) => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/requests/${req.id}/co-agents`, { co_agent_ids: newIds });
            onSaved?.(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Opslaan mislukt");
        }
        setSaving(false);
    };

    const add = (agent) => {
        const newIds = [...(req.co_agent_ids || []), agent.id];
        update(newIds);
        setSearch("");
        setOpen(false);
        toast.success(`${agent.name || agent.email} toegevoegd`);
    };

    const remove = (agentId) => {
        const newIds = (req.co_agent_ids || []).filter((i) => i !== agentId);
        update(newIds);
    };

    return (
        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-5 lg:p-6" data-testid="co-agents-manager">
            <div className="flex items-center justify-between mb-3 gap-3">
                <div className="flex items-center gap-2">
                    <Users className="h-3.5 w-3.5 text-[#E5A93D]" />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Extra makelaars op dit project</span>
                </div>
                {saving && <Loader2 className="h-3.5 w-3.5 text-zinc-500 animate-spin" />}
            </div>

            {/* Existing co-agents */}
            {currentList.length > 0 ? (
                <div className="flex flex-wrap gap-2 mb-3">
                    {currentList.map((a) => (
                        <div
                            key={a.id}
                            className="inline-flex items-center gap-2 h-9 pl-2 pr-1 bg-[#F4F4F5] border border-zinc-200 rounded-sm text-sm text-zinc-950"
                            data-testid={`co-agent-chip-${a.id}`}
                        >
                            {a.logo_url ? (
                                <img
                                    src={a.logo_url.startsWith("http") ? a.logo_url : `${process.env.REACT_APP_BACKEND_URL}${a.logo_url}`}
                                    alt=""
                                    className="h-6 w-6 rounded-sm object-contain bg-zinc-100"
                                />
                            ) : (
                                <div className="h-6 w-6 rounded-sm bg-[#E5A93D]/15 text-[#E5A93D] flex items-center justify-center text-[10px] font-bold">
                                    {(a.name || a.email || "?").slice(0, 1).toUpperCase()}
                                </div>
                            )}
                            <div className="leading-tight pr-1">
                                <div className="text-xs font-medium truncate max-w-[180px]">{a.name || a.email}</div>
                                {a.company && <div className="text-[10px] text-zinc-500 truncate max-w-[180px]">{a.company}</div>}
                            </div>
                            <button
                                onClick={() => remove(a.id)}
                                disabled={saving}
                                className="ml-1 h-7 w-7 flex items-center justify-center rounded-sm text-zinc-500 hover:text-red-600 hover:bg-red-500/10 transition disabled:opacity-50"
                                data-testid={`co-agent-remove-${a.id}`}
                                title="Verwijderen"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-xs text-zinc-500 italic mb-3">Nog geen extra makelaars toegevoegd.</div>
            )}

            {/* Add picker */}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        disabled={saving}
                        className="inline-flex items-center gap-2 h-9 px-3 rounded-sm text-xs font-medium border border-zinc-200 bg-[#F4F4F5] text-zinc-800 hover:text-zinc-950 hover:border-[#E5A93D]/40 hover:bg-[#E5A93D]/5 transition active:scale-[0.98] disabled:opacity-50"
                        data-testid="co-agent-add-btn"
                    >
                        <UserPlus className="h-3.5 w-3.5 text-[#E5A93D]" />
                        Makelaar toevoegen
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    sideOffset={6}
                    className="w-[360px] bg-[#FFFFFF] border-zinc-200 text-zinc-950 p-0"
                    data-testid="co-agent-picker"
                >
                    <div className="p-3 border-b border-zinc-100">
                        <div className="relative">
                            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                            <input
                                autoFocus
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Zoek op naam, email of kantoor…"
                                className="w-full h-9 pl-8 pr-3 bg-[#F4F4F5] border border-zinc-200 rounded-sm text-xs text-zinc-950 placeholder:text-zinc-500 focus:outline-none focus:border-[#E5A93D]/50"
                                data-testid="co-agent-search-input"
                            />
                        </div>
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                        {filtered.length === 0 ? (
                            <div className="px-3 py-6 text-center text-xs text-zinc-500" data-testid="co-agent-empty">
                                {search ? "Geen makelaars gevonden." : "Geen beschikbare makelaars."}
                            </div>
                        ) : (
                            filtered.map((a) => (
                                <button
                                    key={a.id}
                                    onClick={() => add(a)}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#E5A93D]/10 border-b border-zinc-100 last:border-b-0 text-left transition"
                                    data-testid={`co-agent-option-${a.id}`}
                                >
                                    {a.logo_url ? (
                                        <img
                                            src={a.logo_url.startsWith("http") ? a.logo_url : `${process.env.REACT_APP_BACKEND_URL}${a.logo_url}`}
                                            alt=""
                                            className="h-8 w-8 rounded-sm object-contain bg-zinc-100 flex-shrink-0"
                                        />
                                    ) : (
                                        <div className="h-8 w-8 rounded-sm bg-[#E5A93D]/15 text-[#E5A93D] flex items-center justify-center text-xs font-bold flex-shrink-0">
                                            {(a.name || a.email || "?").slice(0, 1).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <div className="text-xs font-medium text-zinc-950 truncate">{a.name || a.email}</div>
                                        <div className="text-[10px] text-zinc-500 truncate">{a.company ? `${a.company} · ` : ""}{a.email}</div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}
